import React, { ReactElement } from "react"
import { useStateMachine } from "../../../context/stateMachine/StateMachineV2"
import { useGetMedicalInfo, useMedicalInfo } from "../../context/MedicalInfoContext"
import { StepLayout } from "#/components/carrot-plans/shared/StepLayout"
import { ButtonFooterLayout, ContinueButton } from "#/components/carrot-plans/shared/ButtonFooter"
import { FormattedMessage, useIntl } from "react-intl"
import { Box } from "@carrotfertility/carotene-core"
import { Form } from "@carrotfertility/carotene-core-x"
import { RoutingFlowTitle } from "#/components/carrot-plans/shared"
import { useParams } from "react-router-dom"
import useCarrotPlan from "../../hooks/useCarrotPlan"
import { CarrotPlanStep } from "#/types/carrotPlanTypes"
import { useCheckRoutingInfoQueryCache } from "#/components/carrot-plans/shared/useQueryRoutingInfo"
import { useCalibratedEngagementEnabled } from "#/hooks/calibratedEngagement/useCalibratedEngagement"
import { CenteredLoadingIndicator } from "#/components/views/molecules/Molecules"
import { useAmabAfabMedicalPreference } from "#/components/carrot-plans/hooks/useMedicalInfoFlow"
import { AmabAfabMedicalPreference } from "#/components/carrot-plans/steps/medical-questions/medicalFlowConstants"

export default function WelcomeStep(): ReactElement {
  const intl = useIntl()
  const { data: currentCarrotPlan, isLoading: isCarrotPlanLoading } = useCarrotPlan()
  const { startMedicalInfo } = useMedicalInfo()
  const { data: medicalInfo, isLoading: isMedicalInfoLoading } = useGetMedicalInfo(false)
  const amabAfabMedicalPreference = useAmabAfabMedicalPreference()
  const { journey: journeyParam } = useParams<{ journey: string }>()
  const routingInfo = useCheckRoutingInfoQueryCache()
  const calibratedEngagementEnabledQuery = useCalibratedEngagementEnabled()

  const medicalInfoStartedFromMenuItem = journeyParam != null
  const medicalInfoInProgress = currentCarrotPlan?.steps.some(
    (step: CarrotPlanStep) => step.carrotPlanStepId === medicalInfo?.medicalCarrotPlanStepId
  )

  const skipWelcomeScreenIfMedicalInfoInProgress = (): string => {
    const skip = ""
    const doNotSkip: string | null = null
    if (!isCarrotPlanLoading && !isMedicalInfoLoading) {
      if (medicalInfoStartedFromMenuItem) {
        return medicalInfoInProgress ? skip : doNotSkip
      }
      return medicalInfo ? skip : doNotSkip
    }
  }

  const { send } = useStateMachine(skipWelcomeScreenIfMedicalInfoInProgress())

  async function onClickContinue(): Promise<void> {
    if (medicalInfoStartedFromMenuItem && !medicalInfoInProgress) {
      await startMedicalInfo({ fromMedicalMenuItem: true, amabAfabMedicalPreference })
    }
    send("")
  }

  const header =
    routingInfo?.amabAfabMedicalPreference === AmabAfabMedicalPreference.AUTO_SKIPPED
      ? intl.formatMessage({
          defaultMessage:
            "Next, we'll ask some health questions. Any answers you choose to provide will help us guide you to the best care."
        })
      : calibratedEngagementEnabledQuery.data
      ? intl.formatMessage({
          defaultMessage: "Thanks. Any answers you choose to provide will help us support you better."
        })
      : intl.formatMessage({
          defaultMessage: "Thanks. Any questions you choose to answer will help us guide you to the best care."
        })

  const isLoading = calibratedEngagementEnabledQuery.isLoading || isCarrotPlanLoading || isMedicalInfoLoading

  return isLoading ? (
    <CenteredLoadingIndicator />
  ) : (
    <>
      <RoutingFlowTitle title={header} />
      <Box display="flex" justifyContent="center" marginBottom={(theme) => theme.spacing(theme.tokens.spacing.lg)}>
        <Box
          component="img"
          src={`/images/illo-yir-telehealth.png`}
          srcSet={`/images/illo-yir-telehealth-2x.png`}
          alt=""
          marginBottom={(theme) => theme.spacing(theme.tokens.spacing.md)}
          width={228}
          height={228}
        />
      </Box>
      <StepLayout
        header={header}
        formElements={
          <Form onSubmit={onClickContinue}>
            <ButtonFooterLayout
              continueButton={
                <ContinueButton>
                  <FormattedMessage defaultMessage="Continue" />
                </ContinueButton>
              }
            />
          </Form>
        }
      />
    </>
  )
}
